import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import {
  ControlsOf,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
} from '@ngneat/reactive-forms';
import { takeUntil } from 'rxjs/operators';

import {
  booleanToYesNo,
  yesNoToBoolean,
} from '@digital-platform/shared/domain';
import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';

interface FormElements {
  isCustomer3rdParty: boolean;
  is3rdPartyAuthorized: boolean;
}
interface FormElementsInternal {
  isCustomer3rdParty: 'yes' | 'no';
  is3rdPartyAuthorized: 'yes' | 'no';
}
@Component({
  selector: 'digital-platform-generator-third-party-questions',
  templateUrl: './generator-third-party-questions.component.html',
  styleUrls: ['./generator-third-party-questions.component.scss'],
})
export class GeneratorThirdPartyQuestionsComponent
  extends NestedFormComponent<FormElements, FormElementsInternal>
  implements OnInit, OnChanges, ControlValueAccessor<FormElements>
{
  @Input() public displayMode: 'customer' | 'admin';
  @Input() profileStatus: string;
  form: FormGroup<ControlsOf<FormElementsInternal>>;

  constructor(
    private formBuilder: FormBuilder,
    @Optional() @Self() public controlDir: NgControl
  ) {
    super(controlDir);
  }

  ngOnInit(): void {
    const formElements = this.value;
    this.initializeForm(this.mapInputValue(formElements));
    super.ngOnInit();
    this.onQuestionSelect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.form.controls.isCustomer3rdParty.removeValidators(
        Validators.required
      );
      this.form.controls.is3rdPartyAuthorized.removeValidators(
        Validators.required
      );
    } else if (
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      this.form?.controls.isCustomer3rdParty.addValidators(Validators.required);
      this.form?.controls.is3rdPartyAuthorized.addValidators(
        FormValidators.requiredIf(
          () =>
            (this.form.get('isCustomer3rdParty').value === 'yes' &&
              this.displayMode === 'customer') ||
            (this.displayMode === 'admin' &&
              this.profileStatus !== 'Not Submitted')
        )
      );
    }
  }

  private initializeForm(form: FormElementsInternal) {
    this.form = this.formBuilder.group({
      isCustomer3rdParty: [form?.isCustomer3rdParty, Validators.required],
      is3rdPartyAuthorized: [
        form?.is3rdPartyAuthorized,
        FormValidators.requiredIf(
          () =>
            (this.form.get('isCustomer3rdParty').value === 'yes' &&
              this.displayMode === 'customer') ||
            (this.displayMode === 'admin' &&
              this.profileStatus !== 'Not Submitted')
        ),
      ],
    });
  }

  errorHandling = (control: string, error: string) =>
    this.form.controls[control].hasError(error);

  public mapInputValue(value: FormElements): FormElementsInternal {
    return {
      isCustomer3rdParty: booleanToYesNo(value?.isCustomer3rdParty),
      is3rdPartyAuthorized: booleanToYesNo(value?.is3rdPartyAuthorized),
    };
  }
  public mapOutputValue(value: FormElementsInternal): FormElements {
    return {
      isCustomer3rdParty: yesNoToBoolean(value?.isCustomer3rdParty),
      is3rdPartyAuthorized:
        value.isCustomer3rdParty === 'no'
          ? false
          : yesNoToBoolean(value?.is3rdPartyAuthorized),
    };
  }

  onQuestionSelect = () => {
    const IsCustomer3rdPartyControl = this.form.get('isCustomer3rdParty');
    const IsCustomerAuthControl = this.form.get('is3rdPartyAuthorized');
    const updateValidationState = (val) => {
      if (val === 'no') {
        IsCustomerAuthControl.setValue('no');
        IsCustomerAuthControl.disable();
        IsCustomerAuthControl.updateValueAndValidity();
      }
      if (this.form.touched || this.form.dirty) {
        if (val === 'no') {
          IsCustomerAuthControl.setValue('no');
          IsCustomerAuthControl.disable();
          IsCustomerAuthControl.updateValueAndValidity();
        } else {
          IsCustomerAuthControl.reset();
          IsCustomerAuthControl.enable();
          IsCustomerAuthControl.updateValueAndValidity();
        }
      }
    };

    updateValidationState(IsCustomer3rdPartyControl.value);
    IsCustomer3rdPartyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };
}
